import { z } from 'zod';
import type { QueryClient } from '@tanstack/react-query';
import { inventoryQuery } from '@/api/queries';
import type { ToolConfig } from './utils/create-tool';

const inputSchema = z.object({
  category: z.string().optional(),
  brand_name: z.string().optional(),
  product_search: z.string().optional(),
  limit: z.number().optional().default(200),
});

async function execute(params: z.infer<typeof inputSchema>, queryClient: QueryClient) {
  const { category, brand_name, product_search, limit } = params;

  const hasFilters = !!(category || brand_name || product_search);
  const filters = hasFilters ? { category, brand_name, product_search } : undefined;
  const packages = await queryClient.fetchQuery(inventoryQuery(filters));

  // Aggregate by ProductId (sum package quantities, keep earliest received / earliest expiration)
  const productMap = new Map<
    number,
    {
      product_name: string;
      sku: string | null;
      category: string | null;
      brand_name: string | null;
      brand_catalog_brand_id: string | null;
      quantity_on_hand: number;
      unit_abbreviation: string | null;
      unit_cost: number | null;
      thc_pct: number | null;
      cbd_pct: number | null;
      is_retired: boolean;
      low_inventory_threshold: number | null;
      earliest_received_date: string | null;
      earliest_expiration_date: string | null;
    }
  >();

  for (const pkg of packages) {
    if (!pkg.product) {
      continue;
    }

    const productId = pkg.product.id;
    const existing = productMap.get(productId);
    if (existing) {
      existing.quantity_on_hand += pkg.quantity ?? 0;
      if (
        pkg.receivedDate &&
        (!existing.earliest_received_date || pkg.receivedDate < existing.earliest_received_date)
      ) {
        existing.earliest_received_date = pkg.receivedDate;
      }
      if (
        pkg.expirationDate &&
        (!existing.earliest_expiration_date ||
          pkg.expirationDate < existing.earliest_expiration_date)
      ) {
        existing.earliest_expiration_date = pkg.expirationDate;
      }
    } else {
      productMap.set(productId, {
        product_name: pkg.product.whseProductsDescription ?? `Product ${productId}`,
        sku: pkg.product.whseProductsProductNo ?? null,
        category: pkg.product.productTypeNavigation?.productType ?? null,
        brand_name: pkg.product.brand?.brandName ?? null,
        brand_catalog_brand_id: pkg.product.brand?.brandCatalogBrandId ?? null,
        quantity_on_hand: pkg.quantity ?? 0,
        unit_abbreviation: pkg.unit?.abbreviation ?? null,
        unit_cost: pkg.product.unitCost ?? null,
        thc_pct: pkg.product.thccontent ?? null,
        cbd_pct: pkg.product.cbdcontent ?? null,
        is_retired: pkg.product.isRetired,
        low_inventory_threshold: pkg.product.lowInventoryThreshold ?? null,
        earliest_received_date: pkg.receivedDate ?? null,
        earliest_expiration_date: pkg.expirationDate ?? null,
      });
    }
  }

  const products = [...productMap.entries()]
    .map(([product_id, info]) => ({
      product_id,
      ...info,
    }))
    .sort((a, b) => a.product_name.localeCompare(b.product_name))
    .slice(0, limit);

  return { products, total: products.length };
}

export const inventoryTool: ToolConfig<typeof inputSchema> = {
  name: 'get_inventory',
  description:
    'Retrieves current inventory with product, brand, category, and quantity. Supports server-side filtering by category, brand name, or product name. Use this when the user asks about stock levels, looks up specific products, or wants to see what they carry for a brand.',
  inputSchema,
  execute,
};
